import {
  BookOutlined,
  CalendarOutlined,
  FileTextOutlined,
  PhoneOutlined,
  RiseOutlined,
  RollbackOutlined,
  ShoppingOutlined,
  SwapOutlined,
  TagOutlined,
  UndoOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { Modal } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";

/* ════════════════════════════════════════════════════════════════
   DATA LAYER  (no UI here)
   ════════════════════════════════════════════════════════════════ */

// Base URL of the retail ERP API (from the URLs you shared).
// If your Config already exports this same base, import it instead.
const API_BASE = "https://retailtextiles.timeserasoftware.in";

// Shown only in the dialog header
const currentDate = new Date().toLocaleDateString("en-US", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * One entry per summary card.
 *
 *  table        – table name sent to the API
 *  fetchAll     – true  → GetDataFromGivenTableName (no `where`), today's rows
 *                          are picked client-side using `dateCol`
 *                 false → GetDataFromGivenTableNameWithWhere with a date filter
 *  dateCol      – date column used for "today"
 *  extraWhere   – extra SQL condition (e.g. type='EB')
 *  amountKeys   – column(s) summed for the card; first one present in a row wins
 *  cancelKey    – rows where this column is "YES" are excluded
 *  dedupeKey    – (optional) keeps one row per value, only if the column exists
 *
 *  ✅ confirmed from your sample rows: sales, exchange, receipts, purchase
 *  ⚠ CONFIRM = column names I could not see (no sample row): saleReturn,
 *    supplierPayments, purchaseReturn, journal.
 */
const SOURCES = {
  /* ✅ CREDIT_BILLMAST — sample row: billno, billdate, CustName, NETAMT, CANCELL … */
  sales: {
    table: "CREDIT_BILLMAST",
    dateCol: "billdate",
    amountKeys: ["NETAMT", "totamt"],
    balanceKey: "BALANCE",
    cancelKey: "CANCELL",
    idKey: "billno",
    titleKey: "CustName",
    fallbackTitle: "Walk-in Customer",
    titleLabel: "Customer",
    subKeys: ["PhoneNo", "billdate", "totqty", "billno"],
    amountLabel: "Net Amount",
    emptyIcon: "🧾",
    emptyText: "No sales recorded today",
  },

  /* ✅ BILL_ITEMS_DET with type='EB' — sample row: billno, billdate, itemname, qty, amount … */
  exchange: {
    table: "BILL_ITEMS_DET",
    extraWhere: "type='EB'",
    dateCol: "billdate",
    amountKeys: ["amount", "BASICAMT"],
    idKey: "billno",
    titleKey: "itemname",
    fallbackTitle: "Exchange Item",
    titleLabel: "Item",
    subKeys: ["billno", "billdate", "qty"],
    amountLabel: "Amount",
    emptyIcon: "🔁",
    emptyText: "No exchanges recorded today",
  },

  /* ⚠ RETURN_MAST — fetched with GetDataFromGivenTableName (no where), so
       today's rows are filtered here. Column names are GUESSED from the
       CREDIT_BILLMAST layout — confirm with one RETURN_MAST row. */
  saleReturn: {
    table: "RETURN_MAST",
    fetchAll: true,
    dateCol: "billdate", // ⚠ CONFIRM
    amountKeys: ["NETAMT", "totamt"], // ⚠ CONFIRM
    cancelKey: "CANCELL", // ⚠ CONFIRM
    idKey: "billno", // ⚠ CONFIRM
    titleKey: "CustName", // ⚠ CONFIRM
    fallbackTitle: "Walk-in Customer",
    titleLabel: "Customer",
    subKeys: ["PhoneNo", "billdate", "totqty", "billno"],
    amountLabel: "Net Amount",
    emptyIcon: "↩️",
    emptyText: "No sale returns today",
  },

  /* ✅ RECEIPTS — CREDIT_REC_INFO, filtered by RecDate = today, sum of paidamt.
       Sample row: custname, RecNo, RecDate, paidamt, MODE, CANCELLED … */
  receipts: {
    table: "CREDIT_REC_INFO",
    dateCol: "RecDate",
    dedupeKey: "RecNo", // one row per receipt number
    amountKeys: ["paidamt", "PAIDAMT"],
    cancelKey: "CANCELLED",
    idKey: "RecNo",
    titleKey: "custname",
    fallbackTitle: "Customer",
    titleLabel: "Customer",
    subKeys: ["RecNo", "RecDate", "MODE"],
    amountLabel: "Paid Amt",
    emptyIcon: "📄",
    emptyText: "No receipts recorded today",
  },

  /* ⚠ SUPPLIER PAYMENTS — PARTY_RECEIPT_INFO, RecDate = today, sum of paidamt.
       Same layout as CREDIT_REC_INFO is ASSUMED (no sample row yet).
       No dedupeKey on purpose, so a wrong guess can't collapse rows.
       Add dedupeKey: "RecNo" once you confirm the column exists. */
  supplierPayments: {
    table: "PARTY_RECEIPT_INFO",
    dateCol: "RecDate", // ⚠ CONFIRM
    amountKeys: ["paidamt", "PAIDAMT"], // ⚠ CONFIRM
    cancelKey: "CANCELLED", // ⚠ CONFIRM
    idKey: "RecNo", // ⚠ CONFIRM
    titleKey: "partyname", // ⚠ CONFIRM (falls back to custname / DEALERNAME)
    fallbackTitle: "Supplier",
    titleLabel: "Supplier",
    subKeys: ["RecNo", "RecDate", "MODE"], // ⚠ CONFIRM
    amountLabel: "Paid Amt",
    emptyIcon: "💸",
    emptyText: "No supplier payments today",
  },

  /* ✅ PURCHASE — PURCHINVOICE_MAST, INVDATE = today, sum of netamt.
       Sample row: INVNO, INVDATE, partyname, netamt, cancelled, TOTQTY … */
  purchase: {
    table: "PURCHINVOICE_MAST",
    dateCol: "INVDATE",
    amountKeys: ["netamt", "NETAMT"],
    cancelKey: "cancelled",
    idKey: "INVNO",
    titleKey: "partyname",
    fallbackTitle: "Supplier",
    titleLabel: "Supplier",
    subKeys: ["INVNO", "INVDATE", "TOTQTY"],
    amountLabel: "Net Amount",
    emptyIcon: "📦",
    emptyText: "No purchases recorded today",
  },

  /* ⚠ PURCHASE RETURN — PURCH_RTN_MAST, RTNDATE = today, sum of netamt.
       Column names other than RTNDATE / netamt are ASSUMED from
       PURCHINVOICE_MAST — confirm with one row. */
  purchaseReturn: {
    table: "PURCH_RTN_MAST",
    dateCol: "RTNDATE",
    amountKeys: ["netamt", "NETAMT"],
    cancelKey: "cancelled", // ⚠ CONFIRM
    idKey: "RTNNO", // ⚠ CONFIRM
    titleKey: "partyname", // ⚠ CONFIRM
    fallbackTitle: "Supplier",
    titleLabel: "Supplier",
    subKeys: ["RTNNO", "RTNDATE"], // ⚠ CONFIRM
    amountLabel: "Net Amount",
    emptyIcon: "📤",
    emptyText: "No purchase returns today",
  },

  /* ── Not yet provided — table / column names are still guesses ── */
  journal: {
    table: "DAY_TRANSACTIONS", // ⚠ CONFIRM
    extraWhere: "TYPE='JOURNAL ENTRY'",
    dateCol: "SDATE",
    amountKeys: ["Amount"],
    idKey: "VoucherNo",
    titleKey: "Description",
    titleLabel: "Particulars",
    subKeys: ["SDATE"],
    amountLabel: "Amount",
    emptyIcon: "📒",
    emptyText: "No journal entries",
  },
};

const asArray = (d) => (Array.isArray(d) ? d : []);

/* first key from `keys` that exists (and isn't null) on the row */
const pickKey = (row, keys) =>
  keys.find(
    (k) =>
      Object.prototype.hasOwnProperty.call(row, k) &&
      row[k] !== null &&
      row[k] !== undefined,
  );

/* numeric amount of a row for a source (strings like "150" are handled) */
const amountOf = (row, src) => {
  const k = pickKey(row, src.amountKeys);
  return k ? Number(row[k]) || 0 : 0;
};

const sumRows = (rows, src) =>
  (rows || []).reduce((a, r) => a + amountOf(r, src), 0);

/* Console hint when a configured column isn't in the API response */
const warnIfColumnsMissing = (key, src, rows) => {
  if (!rows.length) return;
  const row = rows[0];
  const problems = [];
  if (!pickKey(row, src.amountKeys))
    problems.push(`amount column (tried ${src.amountKeys.join(", ")})`);
  if (!Object.prototype.hasOwnProperty.call(row, src.dateCol))
    problems.push(`date column "${src.dateCol}"`);
  if (problems.length) {
    console.warn(
      `Today's Summary → ${key} (${src.table}): missing ${problems.join(" and ")}. ` +
      `Columns returned: ${Object.keys(row).join(", ")}`,
    );
  }
};

const fetchSource = async (key, src, tenantName) => {
  const headers = tenantName ? { tenantName } : {};
  const today = dayjs();
  let rows;

  if (src.fetchAll) {
    // GetDataFromGivenTableName — no `where`, filter to today here
    const res = await axios.get(
      `${API_BASE}/api/ERP/GetDataFromGivenTableName`,
      { params: { tableName: src.table }, headers },
    );
    const all = asArray(res.data);
    warnIfColumnsMissing(key, src, all);
    rows = all.filter((r) => dayjs(r[src.dateCol]).isSame(today, "day"));
  } else {
    // GetDataFromGivenTableNameWithWhere — today >= 00:00 and < tomorrow 00:00
    const start = today.format("YYYY-MM-DD");
    const end = today.add(1, "day").format("YYYY-MM-DD");
    const where = [
      src.extraWhere,
      `${src.dateCol}>='${start}'`,
      `${src.dateCol}<'${end}'`,
    ]
      .filter(Boolean)
      .join(" AND ");
    const res = await axios.get(
      `${API_BASE}/api/ERP/GetDataFromGivenTableNameWithWhere`,
      { params: { tableName: src.table, where }, headers },
    );
    rows = asArray(res.data);
    warnIfColumnsMissing(key, src, rows);
  }

  // Keep one row per dedupeKey — only when that column really exists,
  // so a wrong column name can never collapse everything into one row
  if (
    src.dedupeKey &&
    rows.length &&
    Object.prototype.hasOwnProperty.call(rows[0], src.dedupeKey)
  ) {
    rows = Array.from(new Map(rows.map((i) => [i[src.dedupeKey], i])).values());
  }
  // Cancelled documents are left out of totals and lists
  if (src.cancelKey) {
    rows = rows.filter(
      (r) => String(r[src.cancelKey] ?? "").toUpperCase() !== "YES",
    );
  }
  return rows;
};

/* Fetch every source in parallel; one failing never blocks the rest */
const useTodaySummaryData = () => {
  const [rowsByKey, setRowsByKey] = useState({});
  const tenantName = localStorage.getItem("tenantName");

  useEffect(() => {
    Object.entries(SOURCES).forEach(async ([key, src]) => {
      try {
        const rows = await fetchSource(key, src, tenantName);
        setRowsByKey((prev) => ({ ...prev, [key]: rows }));
      } catch (e) {
        console.error(`Today's Summary → ${key} (${src.table}) failed:`, e);
        setRowsByKey((prev) => ({ ...prev, [key]: [] }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return rowsByKey;
};

/* ════════════════════════════════════════════════════════════════
   UI
   ════════════════════════════════════════════════════════════════ */

/* ── fade-up stagger ── */
const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.07, duration: 0.4, ease: "easeOut" },
  }),
};

/* ── Uniform compact card ── */
const MiniCard = ({ icon, label, value, subValue, accent, index, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      whileHover={{ y: -3, boxShadow: "0 8px 24px rgba(44,27,105,0.16)" }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      onClick={onClick}
      style={{
        background: "#f6f5f5",
        borderRadius: 12,
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        boxShadow: "0 2px 10px rgba(44,27,105,0.08)",
        cursor: "pointer",
        borderLeft: `4px solid ${accent}`,
        transition: "box-shadow 0.25s",
        minHeight: 62,
      }}
    >
      {/* Icon bubble */}
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: hovered ? accent : `${accent}28`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 0.25s ease-out",
        }}
      >
        {React.cloneElement(icon, {
          style: {
            ...icon.props?.style,
            color: hovered ? "#fff" : accent,
            fontSize: icon.props?.style?.fontSize ?? 16,
            transition: "color 0.25s",
          },
        })}
      </div>

      {/* Text */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: "#6a6969",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </div>
        {subValue && (
          <div
            style={{
              fontSize: 10,
              color: accent,
              fontWeight: 700,
              marginTop: 1,
            }}
          >
            {subValue}
          </div>
        )}
        <div
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: "#2d1b69",
            marginTop: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {value}
        </div>
      </div>
    </motion.div>
  );
};

/* ── format currency ── */
const fmt = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const WEIGHT_KEYS = new Set([
  "TotGwt",
  "TotNwt",
  "TotWastage",
  "TotFGold",
  "TotWeight",
  "GROSSWT",
]);
const DATE_KEYS = new Set([
  "BillDate",
  "billdate",
  "RepDate",
  "RecDate",
  "PDATE",
  "VoucherDate",
  "DELIVERYDATE",
  "OrdDate",
  "DelDate",
  "INVDATE",
  "RTNDATE",
  "SDATE",
]);
const MOBILE_KEYS = new Set([
  "MobileNum",
  "MobileNo",
  "MOBILENUM",
  "MobileNum2",
  "Phno",
  "mobilenum",
  "PhoneNo",
]);

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "—";

const fmtSubValue = (key, value) => {
  if (DATE_KEYS.has(key)) return fmtDate(value);
  if (WEIGHT_KEYS.has(key)) return `${Number(value || 0).toFixed(3)} g`;
  if (key === "TotPieces" || key === "TotPcs") return `${value} pcs`;
  if (key === "totqty" || key === "qty") return `${Number(value)} pcs`;
  if (key === "TOTQTY") return `Qty ${Number(value || 0)}`;
  if (key === "billno") return `Bill #${value}`;
  if (key === "INVNO") return `Inv #${value}`;
  if (key === "RecNo") return `Rec #${value}`;
  if (key === "RTNNO") return `Rtn #${value}`;
  return String(value);
};

const subIcon = (key) => {
  if (MOBILE_KEYS.has(key)) return <PhoneOutlined style={{ fontSize: 9 }} />;
  if (DATE_KEYS.has(key)) return <CalendarOutlined style={{ fontSize: 9 }} />;
  return <TagOutlined style={{ fontSize: 9 }} />;
};

/* "-", "undefined" and blanks count as empty */
const clean = (v) => {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s === "" || s === "-" || s === "undefined" ? null : v;
};

const RecordCard = ({ record, index, config, accent }) => {
  const title =
    clean(record[config.titleKey]) ||
    clean(record.CustName) ||
    clean(record.custname) ||
    clean(record.CustomerName) ||
    clean(record.partyname) ||
    clean(record.DEALERNAME) ||
    clean(record.DealerName) ||
    config.fallbackTitle ||
    `Record ${index + 1}`;
  const amount = amountOf(record, config);
  const balance = config.balanceKey ? record[config.balanceKey] : undefined;
  const isCancelled =
    config.cancelKey &&
    String(record[config.cancelKey] || "").toUpperCase() === "YES";

  const subInfo = (config.subKeys || [])
    .map((key) => ({ key, value: record[key] }))
    .filter(({ value }) => clean(value) !== null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: Math.min(index * 0.04, 0.5),
        duration: 0.32,
        ease: "easeOut",
      }}
      whileHover={{ y: -2, boxShadow: `0 6px 20px ${accent}25` }}
      style={{
        background: "#fff",
        borderRadius: 12,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        boxShadow: "0 1px 6px rgba(44,27,105,0.06)",
        border: `1px solid ${accent}22`,
        borderLeft: `3px solid ${accent}`,
        transition: "box-shadow 0.2s",
      }}
    >
      {/* S.No bubble */}
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          flexShrink: 0,
          background: `${accent}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 800,
          color: accent,
        }}
      >
        {index + 1}
      </div>

      {/* Title + sub-info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: "#2d1b69",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <UserOutlined style={{ fontSize: 10, color: accent }} />
          {title}
          {isCancelled && (
            <span
              style={{
                fontSize: 8,
                fontWeight: 700,
                color: "#e05252",
                background: "#fff0f0",
                borderRadius: 6,
                padding: "1px 6px",
                textTransform: "uppercase",
                letterSpacing: 0.3,
                flexShrink: 0,
              }}
            >
              Cancelled
            </span>
          )}
        </div>
        <div
          style={{ display: "flex", gap: 10, marginTop: 3, flexWrap: "wrap" }}
        >
          {subInfo.map(({ key, value }) => (
            <span
              key={key}
              style={{
                fontSize: 10,
                color: "#888",
                display: "flex",
                alignItems: "center",
                gap: 3,
              }}
            >
              {subIcon(key)}
              {fmtSubValue(key, value)}
            </span>
          ))}
        </div>
      </div>

      {/* Amount badge */}
      <div
        style={{
          textAlign: "right",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 4,
        }}
      >
        <div
          style={{
            background: `${accent}15`,
            borderRadius: 8,
            padding: "4px 10px",
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: "#aaa",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {config.amountLabel}
          </div>
          <div style={{ fontSize: 14, fontWeight: 800, color: accent }}>
            {fmt(amount)}
          </div>
        </div>
        {balance !== undefined && Number(balance) !== 0 && (
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: Number(balance) > 0 ? "#e05252" : "#00b894",
            }}
          >
            Bal: {fmt(balance)}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const RecordsDialog = ({ open, onClose, title, icon, accent, rows, config }) => {
  const grandTotal = sumRows(rows, config);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={620}
      centered
      destroyOnClose
      styles={{
        mask: {
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(44,27,105,0.18)",
        },
        content: { borderRadius: 18, padding: 0, overflow: "hidden" },
        body: { padding: 0 },
      }}
      closeIcon={
        <motion.span
          whileHover={{ rotate: 90, scale: 1.1 }}
          transition={{ duration: 0.2 }}
          style={{
            fontSize: 16,
            color: "#7c5cbf",
            display: "flex",
            alignItems: "center",
          }}
        >
          ✕
        </motion.span>
      }
    >
      {/* ── Modal header ── */}
      <div
        style={{
          background: `linear-gradient(135deg,#2d1b69 0%,${accent} 100%)`,
          padding: "18px 22px 16px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -30,
            right: -30,
            width: 100,
            height: 100,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -20,
            left: -20,
            width: 70,
            height: 70,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.06)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <motion.div
              initial={{ rotate: -10, scale: 0.8 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              style={{
                width: 38,
                height: 38,
                borderRadius: 11,
                background: "rgba(255,255,255,0.18)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon &&
                React.cloneElement(icon, {
                  style: { fontSize: 18, color: "#fff" },
                })}
            </motion.div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>
                {title}
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: "rgba(255,255,255,0.65)",
                  marginTop: 1,
                }}
              >
                {currentDate} · {rows.length} record
                {rows.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* Grand total badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 180, delay: 0.15 }}
            style={{
              background: "rgba(255,255,255,0.15)",
              borderRadius: 10,
              padding: "6px 14px",
              border: "1px solid rgba(255,255,255,0.2)",
              textAlign: "right",
            }}
          >
            <div
              style={{
                fontSize: 9,
                color: "rgba(255,255,255,0.65)",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Grand Total
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>
              {fmt(grandTotal)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Column header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "8px 22px 6px",
          background: "linear-gradient(90deg,#f5f0ff,#ede5ff44)",
          borderBottom: "1px solid #ede5ff",
        }}
      >
        <div style={{ width: 28, flexShrink: 0 }} />
        <div
          style={{
            flex: 1,
            fontSize: 9,
            fontWeight: 700,
            color: "#7c5cbf",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {config?.titleLabel}
        </div>
        <div
          style={{
            minWidth: 90,
            textAlign: "right",
            fontSize: 9,
            fontWeight: 700,
            color: "#7c5cbf",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {config?.amountLabel}
        </div>
      </div>

      {/* ── Card list ── */}
      <div
        style={{
          maxHeight: 380,
          overflowY: "auto",
          padding: "10px 16px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          background: "#f9f7ff",
        }}
      >
        <AnimatePresence>
          {rows.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                textAlign: "center",
                padding: "40px 0",
                color: "#b8a4e8",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <div style={{ fontSize: 36, marginBottom: 8 }}>
                {config?.emptyIcon || "📭"}
              </div>
              {config?.emptyText || "No records found"}
            </motion.div>
          ) : (
            rows.map((record, i) => (
              <RecordCard
                key={`${record[config?.idKey] ?? "row"}-${i}`}
                record={record}
                index={i}
                config={config}
                accent={accent}
              />
            ))
          )}
        </AnimatePresence>
      </div>

      {/* ── Footer ── */}
      <div
        style={{
          padding: "10px 22px",
          borderTop: "1px solid #ede5ff",
          display: "flex",
          justifyContent: "flex-end",
          background: "#fff",
        }}
      >
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.96 }}
          onClick={onClose}
          style={{
            background: `linear-gradient(135deg,#2d1b69,${accent})`,
            color: "#fff",
            border: "none",
            borderRadius: 20,
            padding: "6px 22px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(44,27,105,0.25)",
          }}
        >
          Close
        </motion.button>
      </div>

      <style>{`
        div::-webkit-scrollbar { width: 4px; }
        div::-webkit-scrollbar-track { background: #f5f0ff; border-radius: 4px; }
        div::-webkit-scrollbar-thumb { background: #b8a4e8; border-radius: 4px; }
      `}</style>
    </Modal>
  );
};

/* ── Card display definitions (order = display order) ── */
const CARD_DEFS = [
  { id: "sales", label: "Total Sales", Icon: RiseOutlined, accent: "#e05252" },
  { id: "exchange", label: "Exchange", Icon: SwapOutlined, accent: "#f4a261" },
  {
    id: "saleReturn",
    label: "Sale Return",
    Icon: RollbackOutlined,
    accent: "#9a80d3",
  },
  { id: "receipts", label: "Receipt", Icon: FileTextOutlined, accent: "#4cc9f0" },
  {
    id: "supplierPayments",
    label: "Supplier Payments",
    Icon: WalletOutlined,
    accent: "#4c3494",
  },
  {
    id: "purchase",
    label: "Purchase",
    Icon: ShoppingOutlined,
    accent: "#90be6d",
  },
  {
    id: "purchaseReturn",
    label: "Purchase Return",
    Icon: UndoOutlined,
    accent: "#f9c74f",
  },
  {
    id: "journal",
    label: "Journal Entry",
    Icon: BookOutlined,
    accent: "#577590",
  },
];

const AdvancedDetailsCard = () => {
  const rowsByKey = useTodaySummaryData();
  const [modalKey, setModalKey] = useState(null);

  const activeDef = CARD_DEFS.find((c) => c.id === modalKey);

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
        }}
      >
        {CARD_DEFS.map(({ id, label, Icon, accent }, i) => (
          <MiniCard
            key={id}
            index={i}
            icon={<Icon style={{ fontSize: 15 }} />}
            label={label}
            value={fmt(sumRows(rowsByKey[id], SOURCES[id]))}
            subValue={null}
            accent={accent}
            onClick={() => setModalKey(id)}
          />
        ))}
      </div>

      {activeDef && (
        <RecordsDialog
          open={!!modalKey}
          onClose={() => setModalKey(null)}
          title={activeDef.label}
          icon={<activeDef.Icon />}
          accent={activeDef.accent}
          rows={rowsByKey[activeDef.id] || []}
          config={SOURCES[activeDef.id]}
        />
      )}
    </>
  );
};

export default AdvancedDetailsCard;